// Thin fetch wrapper that injects the user's Supabase JWT as a Bearer
// token. All API workers verify the token + workspace membership before
// doing anything.
//
const DEFAULT_API_BASE_URL = "https://mockingbird.digital";

export function apiUrl(path: string): string {
  const base = (
    import.meta.env.VITE_API_BASE_URL || DEFAULT_API_BASE_URL
  ).replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${cleanPath}`;
}

import { supabase } from "@/lib/supabase";

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("not signed in");
  return { Authorization: `Bearer ${token}` };
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(apiUrl(path), { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return (await res.json()) as T;
}

export interface ApiFile {
  blob: Blob;
  /** Response headers, so a caller can read e.g. an export's truncation flag. */
  headers: Headers;
}

/**
 * GET something that is not JSON — currently the audit CSV export.
 *
 * A file download would normally be a plain `<a href>`, but these endpoints
 * authenticate with a Bearer header and a top-level navigation cannot carry
 * one. Putting the token in the URL instead would leak it into history, logs
 * and the Referer header. So we fetch with the header and hand the caller a
 * Blob to attach to a real anchor the user then clicks.
 */
export async function apiGetFile(path: string): Promise<ApiFile> {
  const headers = await authHeaders();
  const res = await fetch(apiUrl(path), { headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return { blob: await res.blob(), headers: res.headers };
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const headers = {
    ...(await authHeaders()),
    "Content-Type": "application/json",
  };
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return (await res.json()) as T;
}
